import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, Milestone, Plus, Trash2, Undo2 } from 'lucide-react'

import { Field } from '../../components/Field'
import { MAX_VIA } from '../../lib/route'
import type { LatLon, Waypoint } from '../../lib/types'
import { AddressSearch } from './AddressSearch'
import {
  entwurfAus,
  koordinatenSchluessel,
  pruefeEntwurf,
  type KoordinatenEntwurf,
} from './platzKoordinate'
import { useRowKeys } from './rowKeys'
import { appendItem, insertAt, moveItem, patchAt, removeAt } from './stageLists'

/**
 * Die Zwischenpunkte einer Etappe bearbeiten.
 *
 * Sie sind die Antwort auf ein Problem, das man erst sieht, wenn man die
 * gezeichnete Strecke mit dem eigenen Roadbook vergleicht: der Routendienst
 * kennt nur Start und Ziel und nimmt dazwischen, was er für schnell hält. Für
 * Bled - Bovec ist das der Karawankentunnel und der Predel - 108 statt 84
 * Kilometer, durch zwei andere Länder -, während die Tulpen derselben Etappe
 * die Kehren des Vršič beschreiben und der Hinweis „im 2. Gang, Kühlwasser im
 * Auge behalten“ lautet. Ein Zwischenpunkt auf der Passhöhe stellt das gerade.
 *
 * Drei Regeln stecken hier drin:
 *
 * Die Reihenfolge ist die der Fahrt und wird von Hand bestimmt. Sortiert wird
 * nichts - erst über den Pass und dann ins Tal ist eine andere Fahrt als
 * umgekehrt, und was davon gemeint ist, weiss nur der Nutzer.
 *
 * Ein neuer Punkt sitzt zunächst auf dem Etappenziel. Das ist die einzige
 * Koordinate, die an der Strecke nichts ändert - über das Ziel zum Ziel ist
 * derselbe Weg -, und damit die einzige, die man voreintragen darf. Eine 0/0
 * läge im Golf von Guinea, und die Mitte zwischen Start und Ziel wäre eine
 * Behauptung über eine Strasse, die dort vielleicht gar nicht ist.
 *
 * Gerechnet wird nichts von selbst. Ein geänderter Punkt macht die
 * gespeicherte Strecke veraltet, und das sagt der Block darüber; geholt wird
 * sie erst auf Knopfdruck.
 */
export function ViaEditor({
  werte,
  ziel,
  onChange,
}: {
  werte: Waypoint[]
  /** Das Etappenziel - die Koordinate, mit der ein neuer Punkt anfängt. */
  ziel: LatLon
  onChange: (next: Waypoint[]) => void
}) {
  const keys = useRowKeys(werte.length)
  const [zurueck, setZurueck] = useState<{ index: number; punkt: Waypoint } | null>(null)
  const voll = werte.length >= MAX_VIA

  function entfernen(index: number) {
    const punkt = werte[index]
    if (!punkt) return

    keys.remove(index)
    setZurueck({ index, punkt })
    onChange(removeAt(werte, index))
  }

  function zurueckholen() {
    if (!zurueck) return

    keys.insert(zurueck.index)
    onChange(insertAt(werte, zurueck.index, zurueck.punkt))
    setZurueck(null)
  }

  function anhaengen() {
    if (voll) return

    keys.append()
    // Nach dem Anhängen zeigt der gemerkte Index woandershin, also fallen
    // lassen. Dasselbe gilt beim Verschieben.
    setZurueck(null)
    onChange(appendItem(werte, { name: '', at: [ziel[0], ziel[1]] }))
  }

  function verschieben(index: number, richtung: -1 | 1) {
    keys.move(index, richtung)
    setZurueck(null)
    onChange(moveItem(werte, index, richtung))
  }

  return (
    <section className="inhalt-block">
      <h5 className="block-titel">
        <Milestone size={13} /> Zwischenpunkte
      </h5>

      {werte.length === 0 && (
        <p className="leer">
          Keine — die Strecke nimmt den Weg, den der Dienst für den schnellsten hält. Für einen Pass
          ist das oft nicht der gemeinte: hier eine Stelle eintragen, über die gefahren wird.
        </p>
      )}

      <ul className="eintragsliste">
        {werte.map((punkt, i) => (
          <li key={keys.at(i)} className="eintrag">
            <span className="eintrag-nummer">Punkt {i + 1}</span>

            <Field label="Name">
              <input
                value={punkt.name}
                autoComplete="off"
                placeholder="z. B. Vršič-Pass"
                onChange={(e) => onChange(patchAt(werte, i, { name: e.target.value }))}
              />
            </Field>

            <ViaKoordinate punkt={punkt} onChange={(next) => onChange(patchAt(werte, i, next))} />

            <div className="eintrag-knoepfe">
              <button
                type="button"
                className="knopf still"
                disabled={i === 0}
                onClick={() => verschieben(i, -1)}
                aria-label={`Punkt ${i + 1} nach vorn`}
              >
                <ChevronUp size={14} /> Früher
              </button>
              <button
                type="button"
                className="knopf still"
                disabled={i === werte.length - 1}
                onClick={() => verschieben(i, 1)}
                aria-label={`Punkt ${i + 1} nach hinten`}
              >
                <ChevronDown size={14} /> Später
              </button>
              <button
                type="button"
                className="knopf gefahr"
                onClick={() => entfernen(i)}
                aria-label={`Punkt ${i + 1} entfernen`}
              >
                <Trash2 size={14} /> Entfernen
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="knopfreihe">
        <button type="button" className="knopf" disabled={voll} onClick={anhaengen}>
          <Plus size={14} /> Zwischenpunkt hinzufügen
        </button>
        {zurueck && (
          <button type="button" className="knopf still" onClick={zurueckholen}>
            <Undo2 size={14} /> {beschriftung(zurueck.punkt.name)} zurückholen
          </button>
        )}
      </div>

      {voll && (
        <p className="leer">
          Mehr als {MAX_VIA} Zwischenpunkte nimmt der Routendienst nicht entgegen. Für einen Pass
          reicht einer: er muss den Weg erzwingen, nicht ihn nachzeichnen.
        </p>
      )}

      {werte.length > 0 && (
        <p className="abschluss">
          Die Strecke wird der Reihe nach über diese Stellen geführt. Von selbst rechnet hier
          nichts: eine geänderte Liste macht eine schon berechnete Strecke veraltet, bis du sie mit
          dem Knopf darunter neu holst.
        </p>
      )}
    </section>
  )
}

/**
 * Wo ein Zwischenpunkt liegt: Adresssuche und zwei Zahlenfelder.
 *
 * Wie bei einem Übernachtungsplatz führen die Zahlenfelder einen eigenen
 * Entwurf, der nur bei einer brauchbaren Koordinate in die Reise geschrieben
 * wird - sonst stünde nach dem ersten Tastendruck in „46.43“ die Breite 4 im
 * Dokument. Anders als dort gibt es hier kein „beide Felder leer heisst keine
 * Koordinate“: ein Zwischenpunkt ohne Ort ist kein Zwischenpunkt. Wer ihn
 * loswerden will, entfernt ihn.
 */
function ViaKoordinate({
  punkt,
  onChange,
}: {
  punkt: Waypoint
  onChange: (patch: Partial<Waypoint>) => void
}) {
  const gesetzt = koordinatenSchluessel(punkt.at)
  const [entwurf, setEntwurf] = useState(() => entwurfAus(gesetzt))

  // Ändert sich die gespeicherte Koordinate von aussen - durch die Adresssuche,
  // durch einen Abgleich, durch das Verschieben eines Punktes -, folgen die
  // Felder.
  useEffect(() => {
    setEntwurf(entwurfAus(gesetzt))
  }, [gesetzt])

  const befund = pruefeEntwurf(entwurf)

  const uebernehmen = (next: KoordinatenEntwurf) => {
    setEntwurf(next)
    const naechster = pruefeEntwurf(next)
    if (naechster.art === 'setzen') onChange({ at: naechster.at })
  }

  return (
    <div className="platz-ort">
      <AddressSearch
        platzhalter="Pass, Ort oder Adresse"
        vorgabe={punkt.name}
        onPick={(at, ort) => {
          // Der Name kommt aus dem Treffer, aber nur wenn noch keiner dasteht
          // und nur sein erster Teil: „Prelaz Vršič, Bovec, Slovenija“ ist als
          // Auswahlhilfe richtig und als Beschriftung eines Routenpunktes auf
          // dem Navigationsgerät zu lang. Ein selbst getippter Name bleibt
          // unangetastet.
          const name =
            punkt.name.trim() === '' ? (ort.name.split(',')[0]?.trim() ?? '') : punkt.name
          onChange({ at, name })
        }}
      />

      <div className="raster-2">
        <Field label="Breite">
          <input
            type="number"
            inputMode="decimal"
            step={0.00001}
            value={entwurf.lat}
            placeholder="z. B. 46.4337"
            onChange={(e) => uebernehmen({ ...entwurf, lat: e.target.value })}
          />
        </Field>
        <Field label="Länge">
          <input
            type="number"
            inputMode="decimal"
            step={0.00001}
            value={entwurf.lon}
            placeholder="z. B. 13.7469"
            onChange={(e) => uebernehmen({ ...entwurf, lon: e.target.value })}
          />
        </Field>
      </div>

      {befund.art !== 'setzen' && (
        <p className="karte-warnung">
          {befund.art === 'unbrauchbar'
            ? 'Diese beiden Zahlen ergeben keinen Ort auf der Erde. Übernommen ist weiterhin die Koordinate von vorher.'
            : 'Ein Feld ist leer. Übernommen wird erst, wenn beide eine Zahl tragen — ein Zwischenpunkt ohne Ort ist keiner.'}
        </p>
      )}
    </div>
  )
}

/** Kurzfassung eines Eintrags für den Zurückholen-Knopf. */
function beschriftung(text: string): string {
  const sauber = text.trim()
  if (sauber === '') return 'Punkt ohne Namen'
  return `„${sauber.length > 24 ? `${sauber.slice(0, 24)}…` : sauber}“`
}
